// Configuration de l'environnement, lue au démarrage de la requête et non au build.
//
// Une même image sert dev, staging et la production : les adresses (API, Auth0,
// Vigilab) viennent des variables d'environnement du conteneur (`env_file` du
// compose de chaque serveur, voir `.env.example`). Le layout racine, rendu côté
// serveur, les lit et les publie dans la page (`RUNTIME_CONFIG_SCRIPT_ID`) ; le
// navigateur les relit de là. Aucune variable `NEXT_PUBLIC_*` : Next les inscrit
// en dur dans le JavaScript au build, ce qui obligeait à reconstruire l'image
// pour chaque environnement.
//
// Seules des valeurs publiques passent par ici : elles finissent dans la page.

export type RuntimeConfig = {
  apiUrl: string;
  auth0Domain: string;
  auth0ClientId: string;
  auth0Audience: string;
  sessionWarningTimeoutMin: number;
  sessionLogoutTimeoutMin: number;
  vigilabApiUrl: string;
  vigilabOrigin: string;
};

export const RUNTIME_CONFIG_SCRIPT_ID = "kleer-runtime-config";

// Variables sans lesquelles l'interface ne peut pas fonctionner.
const REQUIRED = ["API_URL", "AUTH0_DOMAIN", "AUTH0_CLIENT_ID", "AUTH0_AUDIENCE"] as const;

type Env = Record<string, string | undefined>;

function text(env: Env, name: string): string {
  return (env[name] || "").trim();
}

function minutes(env: Env, name: string, fallback: number): number {
  const value = Number(text(env, name));
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

export class MissingRuntimeConfigError extends Error {
  constructor(public readonly missing: string[]) {
    super(
      `Variable(s) d'environnement manquante(s) pour l'interface : ${missing.join(", ")}. ` +
        "Voir .env.example.",
    );
    this.name = "MissingRuntimeConfigError";
  }
}

/** Côté serveur : lit et valide la configuration depuis l'environnement du processus. */
export function readRuntimeConfig(env: Env = process.env): RuntimeConfig {
  const missing = REQUIRED.filter((name) => !text(env, name));
  if (missing.length > 0) {
    throw new MissingRuntimeConfigError([...missing]);
  }
  const apiUrl = text(env, "API_URL").replace(/\/+$/, "");
  return {
    apiUrl,
    auth0Domain: text(env, "AUTH0_DOMAIN"),
    auth0ClientId: text(env, "AUTH0_CLIENT_ID"),
    auth0Audience: text(env, "AUTH0_AUDIENCE"),
    sessionWarningTimeoutMin: minutes(env, "SESSION_WARNING_TIMEOUT_MIN", 28),
    sessionLogoutTimeoutMin: minutes(env, "SESSION_LOGOUT_TIMEOUT_MIN", 30),
    // L'API Vigilab est celle de Kleer, sauf adresse dédiée.
    vigilabApiUrl: (text(env, "VIGILAB_API_URL") || apiUrl).replace(/\/+$/, ""),
    vigilabOrigin: text(env, "VIGILAB_ORIGIN"),
  };
}

/** Sérialise la configuration pour un `<script type="application/json">` : `<` est
 * échappé, une valeur ne peut donc pas fermer la balise. */
export function serializeRuntimeConfig(config: RuntimeConfig): string {
  return JSON.stringify(config).replace(/</g, "\\u003c");
}

let browserConfig: RuntimeConfig | null = null;

/** Configuration de l'environnement, côté serveur comme côté navigateur. */
export function getRuntimeConfig(): RuntimeConfig {
  if (typeof window === "undefined") {
    return readRuntimeConfig();
  }
  if (!browserConfig) {
    const script = document.getElementById(RUNTIME_CONFIG_SCRIPT_ID);
    if (!script?.textContent) {
      throw new Error(`Configuration de l'interface absente de la page (#${RUNTIME_CONFIG_SCRIPT_ID}).`);
    }
    browserConfig = JSON.parse(script.textContent) as RuntimeConfig;
  }
  return browserConfig;
}

/** Adresse de l'API Kleer, sans barre finale. */
export function apiUrl(): string {
  return getRuntimeConfig().apiUrl;
}

/** Origines autorisées à embarquer la modale de désambiguïsation (/vigilab/*) en
 * iframe, pour le header CSP `frame-ancestors` posé par middleware.ts. Lues à chaque
 * requête ; `'none'` sans origine explicite (jamais `*`).
 * Ex: VIGILAB_ALLOWED_ORIGINS="https://app.vigilab.example https://sgg.example" */
export function vigilabFrameAncestors(value: string | undefined = process.env.VIGILAB_ALLOWED_ORIGINS): string {
  const allowedOrigins = (value || "")
    .split(/\s+/)
    .map((origin) => origin.trim())
    .filter((origin) => origin && origin !== "*" && /^https?:\/\//.test(origin));
  return allowedOrigins.length > 0 ? allowedOrigins.join(" ") : "'none'";
}

/** Réservé aux tests : oublie la configuration lue dans la page. */
export function resetRuntimeConfigForTests(): void {
  browserConfig = null;
}
